import React, { useRef } from 'react';
import './ColorPalette.css';

/**
 * ColorPalette — a small Word-style swatch picker.
 *
 * Presentation-only and controlled: it renders preset swatch groups (and an
 * optional "More colours…" native picker) and calls `onChange(hex)` when the
 * user picks one. Placement/visibility is the caller's concern, so it drops
 * cleanly inside a Bootstrap Dropdown.Menu, a popover, or an absolute panel.
 *
 * Markup/classes are the shared `.bib-color-palette__*` set (ColorPalette.css),
 * the same ones the SalesReporting email-editor font-colour menu uses.
 *
 * Props:
 *   value      current hex (e.g. '#0F2D52'); the matching swatch is ringed
 *   onChange   (hex) => void — fired for preset clicks and the custom picker
 *   groups     [{ label, colors: [hex, …] }]  preset swatch groups
 *   showMore   show the "More colours…" native-picker row (default true)
 *   moreLabel  label for that row (default "More colours…")
 */
const THEME_COLORS = [
  '#0F2D52', '#1E7F93', '#5FA8B6', '#0D6EFD',
  '#28A745', '#6F42C1', '#D63384', '#FD7E14',
];
const STANDARD_COLORS = [
  '#000000', '#444444', '#888888', '#CCCCCC',
  '#DC3545', '#FFC107', '#198754', '#0DCAF0',
];

export const DEFAULT_COLOR_GROUPS = [
  { label: 'Theme Colours', colors: THEME_COLORS },
  { label: 'Standard Colours', colors: STANDARD_COLORS },
];

const sameColor = (a, b) =>
  typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();

const ColorPalette = ({
  value,
  onChange,
  groups = DEFAULT_COLOR_GROUPS,
  showMore = true,
  moreLabel = 'More colours…',
  className = '',
}) => {
  const customRef = useRef(null);
  const pick = (hex) => onChange && onChange(hex);

  return (
    <div className={`bib-color-palette-body ${className}`.trim()}>
      {groups.map((group, gi) => (
        <React.Fragment key={group.label || gi}>
          <div className={`bib-color-palette__section-label${gi > 0 ? ' mt-2' : ''}`}>
            {group.label}
          </div>
          <div className="bib-color-palette__grid">
            {group.colors.map((c) => (
              <button
                key={c}
                type="button"
                className={`bib-color-palette__swatch-btn${sameColor(c, value) ? ' is-selected' : ''}`}
                title={c}
                aria-label={c}
                aria-pressed={sameColor(c, value)}
                onClick={() => pick(c)}
              >
                <span className="bib-color-palette__swatch" style={{ background: c }} />
              </button>
            ))}
          </div>
        </React.Fragment>
      ))}

      {showMore && (
        <>
          <button
            type="button"
            className="bib-color-palette__more"
            onClick={() => customRef.current?.click()}
          >
            🎨 {moreLabel}
          </button>
          {/* Hidden — opened by "More colours…". The native picker handles the
              custom RGB UX so we don't ship our own colour wheel. */}
          <input
            type="color"
            ref={customRef}
            value={value || '#000000'}
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
            onChange={(e) => pick(e.target.value)}
          />
        </>
      )}
    </div>
  );
};

export default ColorPalette;
